import * as intrfce from '../Interfaces';
import { validateBlockedTerritory } from './blockedTerritories';

/**
 * Creates a billing address object for API submission
 */
export const createBillingAddressObject = (
  formBillingAddress: intrfce.iAddressFormData,
  addressOptions: any,
  emailAddress: string | null
) => {
  const address: any = {
    // Include id for saved addresses (id > 0) and new/recommended addresses (id === -1)
    // Omit id only when it's undefined/null/0
    ...(formBillingAddress.id !== undefined && formBillingAddress.id !== null && Number(formBillingAddress.id) !== 0
      ? { id: Number(formBillingAddress.id) }
      : {}),
    firstName: formBillingAddress.firstName,
    lastName: formBillingAddress.lastName,
    line1: formBillingAddress.address,
    line2: formBillingAddress.line2,
    city: formBillingAddress.cityValue,
    country: formBillingAddress.countryValue,
    countryName: formBillingAddress.countryName,
    postalCode: formBillingAddress.pincode,
    phoneNumber: formBillingAddress.contact,
    emailAddress: formBillingAddress.email || emailAddress,
  };

  // Include countrySubdivision based on locale configuration
  if (addressOptions.stateDropdown && formBillingAddress.stateValue != null && formBillingAddress.stateValue !== '') {
    // If stateDropdown is enabled and value exists, send the actual value
    address.countrySubdivision = formBillingAddress.stateValue;
  } else {
    // If stateDropdown is disabled or no value, send a single space
    address.countrySubdivision = ' ';
  }

  // Only include county if countyField is enabled and value exists
  if (addressOptions.countyField && formBillingAddress.county != null && formBillingAddress.county !== '') {
    address.county = formBillingAddress.county;
  }

  // Only include addressType if provided
  if (addressOptions.addressBilling) {
    address.addressType = addressOptions.addressBilling;
  }

  return address;
};

/**
 * Creates a shipping address object for API submission
 */
export const createShippingAddressObject = (
  isBilling: boolean,
  formShippingAddress: intrfce.iAddressFormData,
  formBillingAddress: intrfce.iAddressFormData,
  addressOptions: any,
  emailAddress: string | null
) => {
  // Debug: log attn value
  console.log('createShippingAddressObject - formShippingAddress.attn:', formShippingAddress.attn);
  
  if (isBilling) {
    const address: any = {
      // Include id for saved addresses (id > 0) and new/recommended addresses (id === -1)
      // Omit id only when it's undefined/null/0
      ...(formShippingAddress.id !== undefined && formShippingAddress.id !== null && Number(formShippingAddress.id) !== 0
        ? { id: Number(formShippingAddress.id) }
        : {}),
      firstName: formShippingAddress.firstName,
      lastName: formShippingAddress.lastName,
      line1: formShippingAddress.address,
      line2: formShippingAddress.line2,
      city: formShippingAddress.cityValue,
      country: formShippingAddress.countryValue,
      countryName: formShippingAddress.countryName,
      postalCode: formShippingAddress.pincode,
      phoneNumber: formShippingAddress.contact,
      emailAddress: formBillingAddress.email || emailAddress,
    };

    // Only include attn if it has a value
    if (formShippingAddress.attn != null && formShippingAddress.attn.trim() !== '') {
      address.attn = formShippingAddress.attn;
    }

    // Include countrySubdivision based on locale configuration
    if (addressOptions.stateDropdown && formShippingAddress.stateValue != null && formShippingAddress.stateValue !== '') {
      // If stateDropdown is enabled and value exists, send the actual value
      address.countrySubdivision = formShippingAddress.stateValue;
    } else {
      // If stateDropdown is disabled or no value, send a single space
      address.countrySubdivision = ' ';
    }

    // Only include county if countyField is enabled and value exists
    if (addressOptions.countyField && formShippingAddress.county != null && formShippingAddress.county !== '') {
      address.county = formShippingAddress.county;
    }

    // Only include addressType if provided
    if (addressOptions.addressShipping) {
      address.addressType = addressOptions.addressShipping;
    }

    return address;
  } else {
    const address: any = {
      // Include id for saved addresses (id > 0) and new/recommended addresses (id === -1)
      // Omit id only when it's undefined/null/0
      ...(formBillingAddress.id !== undefined && formBillingAddress.id !== null && Number(formBillingAddress.id) !== 0
        ? { id: Number(formBillingAddress.id) }
        : {}),
      firstName: formBillingAddress.firstName,
      lastName: formBillingAddress.lastName,
      line1: formBillingAddress.address,
      line2: formBillingAddress.line2,
      city: formBillingAddress.cityValue,
      country: formBillingAddress.countryValue,
      countryName: formBillingAddress.countryName,
      postalCode: formBillingAddress.pincode,
      phoneNumber: formBillingAddress.contact,
      emailAddress: formBillingAddress.email || emailAddress,
    };

    // Only include attn if it has a value (attn is from shipping address)
    if (formShippingAddress.attn != null && formShippingAddress.attn.trim() !== '') {
      address.attn = formShippingAddress.attn;
    }

    // Include countrySubdivision based on locale configuration
    if (addressOptions.stateDropdown && formBillingAddress.stateValue != null && formBillingAddress.stateValue !== '') {
      // If stateDropdown is enabled and value exists, send the actual value
      address.countrySubdivision = formBillingAddress.stateValue;
    } else {
      // If stateDropdown is disabled or no value, send a single space
      address.countrySubdivision = ' ';
    }

    // Only include county if countyField is enabled and value exists
    if (addressOptions.countyField && formBillingAddress.county != null && formBillingAddress.county !== '') {
      address.county = formBillingAddress.county;
    }

    // Only include addressType if provided
    if (addressOptions.addressBilling) {
      address.addressType = addressOptions.addressBilling;
    }

    return address;
  }
};

/**
 * Creates an address validation object for Google Address Validation API
 */
export const createAddressValidationObject = (
  useShipping: boolean,
  formShippingAddress: intrfce.iAddressFormData,
  formBillingAddress: intrfce.iAddressFormData,
  locale: string
) => {
  const address = useShipping ? formShippingAddress : formBillingAddress;
  
  // Build addressLines array - include line2 if present
  const addressLines = [address.address || ""];
  if (address.line2 && address.line2.trim()) {
    addressLines.push(address.line2);
  }
  
  return {
    addressLines,
    locality: address.cityValue || "",
    administrativeArea: address.stateValue || "",
    postalCode: address.pincode || "",
    regionCode: address.countryValue || "",
    languageCode: locale?.substring(0, 2) || "en",
  };
};

/**
 * Compares two addresses and returns an object with the differences
 */
export const getAddressDifferences = (
  userAddress: intrfce.iAddressFormData,
  recommendedAddress: intrfce.iAddressFormData
): Partial<intrfce.iAddressFormData> => {
  const differences: Partial<intrfce.iAddressFormData> = {};
  
  const fields: (keyof intrfce.iAddressFormData)[] = [
    'address',
    'line2',
    'cityValue',
    'stateValue',
    'pincode',
    'countryValue',
    'countryName',
    'county'
  ];
  
  fields.forEach(field => {
    const userValue = userAddress[field];
    const recommendedValue = recommendedAddress[field];
    
    if (userValue !== recommendedValue) {
      (differences as any)[field] = recommendedValue;
    }
  });
  
  return differences;
};

/**
 * Updates a saved address array by replacing or adding an address with id: 0
 */
/**
 * Updates or creates the id === -1 entry (New Address) in the saved addresses array
 * This is used for recommended addresses and new addresses entered by the user
 */
export const updateSavedAddressWithIdMinus1 = (
  prevAddresses: intrfce.iSavedAddress[] | null,
  newAddress: intrfce.iSavedAddress
): intrfce.iSavedAddress[] => {
  // Ensure the address has id === -1
  const addressWithId = { ...newAddress, id: -1 };
  
  if (prevAddresses && prevAddresses.length > 0) {
    const index = prevAddresses.findIndex((addr) => addr.id === -1);
    if (index !== -1) {
      // Replace existing id === -1 entry
      const newArray = [...prevAddresses];
      newArray[index] = addressWithId;
      return newArray;
    }
    // If no id === -1 entry exists, add it (prepend to show it first)
    return [addressWithId, ...prevAddresses];
  } else {
    // No existing addresses, create array with this address
    return [addressWithId];
  }
};

// Keep the old function for backward compatibility but mark as deprecated
/** @deprecated Use updateSavedAddressWithIdMinus1 instead */
export const updateSavedAddressWithId0 = (
  prevAddresses: intrfce.iSavedAddress[] | null,
  newAddress: intrfce.iSavedAddress
): intrfce.iSavedAddress[] => {
  // Convert to id === -1 and use the new function
  const addressWithId = { ...newAddress, id: -1 };
  return updateSavedAddressWithIdMinus1(prevAddresses, addressWithId);
};

/**
 * Converts a recommended address from Google format to form format
 */
export const convertGoogleAddressToFormFormat = (
  googleAddress: any,
  locale: string
): intrfce.iAddressFormData => {
  return {
    pincode: googleAddress.postalAddress?.postalCode || "",
    address: googleAddress.postalAddress?.addressLines?.[0] || "",
    line2: googleAddress.postalAddress?.addressLines?.[1] || "",
    cityValue: googleAddress.postalAddress?.locality || "",
    stateValue: googleAddress.postalAddress?.administrativeArea || "",
    countryName: getCountryName(googleAddress.postalAddress?.regionCode),
    countryValue: googleAddress.postalAddress?.regionCode || "",
    county: locale !== "en-us" ? googleAddress.postalAddress?.sublocality || "" : '',
  };
};

/**
 * Helper to get country name from country code
 */
const getCountryName = (countryCode: string | undefined): string => {
  // This is a simplified version. You might want to use a more comprehensive country mapping
  const countryMap: { [key: string]: string } = {
    'US': 'United States',
    'CA': 'Canada',
    'GB': 'United Kingdom',
    'DE': 'Germany',
    'FR': 'France',
    // Add more as needed
  };
  
  return countryMap[countryCode || ''] || countryCode || '';
};

/**
 * Validates if an address is in a blocked territory
 * 
 * @param address - The address to validate
 * @param locale - The current locale
 * @returns Object with isBlocked flag and error message if blocked
 */
export const validateAddressTerritory = (
  address: intrfce.iAddressFormData,
  locale: string
): { isBlocked: boolean; errorMessage?: string; territoryName?: string } => {
  const { pincode, countryValue } = address;
  
  if (!pincode || !countryValue) {
    return { isBlocked: false };
  }
  
  const result = validateBlockedTerritory(pincode, countryValue, locale);
  
  if (result.isBlocked) {
    return {
      isBlocked: true,
      errorMessage: 'Sorry, this region is currently not supported.',
      territoryName: result.territoryName
    };
  }
  
  return { isBlocked: false };
};

/**
 * List of allowed US states for partners that exclude Alaska/Hawaii.
 */
const ALLOWED_US_STATES_EXCLUDING_AK_HI = [
  'Alabama',
  'Arizona',
  'Arkansas',
  'California',
  'Colorado',
  'Connecticut',
  'Delaware',
  'District Of Columbia',
  'Florida',
  'Georgia',
  'Idaho',
  'Illinois',
  'Indiana',
  'Iowa',
  'Kansas',
  'Kentucky',
  'Louisiana',
  'Maine',
  'Maryland',
  'Massachusetts',
  'Michigan',
  'Minnesota',
  'Mississippi',
  'Missouri',
  'Montana',
  'Nebraska',
  'Nevada',
  'New Hampshire',
  'New Jersey',
  'New Mexico',
  'New York',
  'North Carolina',
  'North Dakota',
  'Ohio',
  'Oklahoma',
  'Oregon',
  'Pennsylvania',
  'Rhode Island',
  'South Carolina',
  'South Dakota',
  'Tennessee',
  'Texas',
  'Utah',
  'Vermont',
  'Virginia',
  'Washington',
  'West Virginia',
  'Wisconsin',
  'Wyoming'
];

/**
 * List of allowed US states including Alaska and Hawaii
 */
const ALLOWED_US_STATES = [
  ...ALLOWED_US_STATES_EXCLUDING_AK_HI,
  'Alaska',
  'Hawaii'
];

/**
 * State abbreviation to full name mapping
 */
const STATE_ABBREVIATIONS: { [key: string]: string } = {
  'AL': 'Alabama',
  'AK': 'Alaska',
  'AZ': 'Arizona',
  'AR': 'Arkansas',
  'CA': 'California',
  'CO': 'Colorado',
  'CT': 'Connecticut',
  'DE': 'Delaware',
  'DC': 'District Of Columbia',
  'FL': 'Florida',
  'GA': 'Georgia',
  'HI': 'Hawaii',
  'ID': 'Idaho',
  'IL': 'Illinois',
  'IN': 'Indiana',
  'IA': 'Iowa',
  'KS': 'Kansas',
  'KY': 'Kentucky',
  'LA': 'Louisiana',
  'ME': 'Maine',
  'MD': 'Maryland',
  'MA': 'Massachusetts',
  'MI': 'Michigan',
  'MN': 'Minnesota',
  'MS': 'Mississippi',
  'MO': 'Missouri',
  'MT': 'Montana',
  'NE': 'Nebraska',
  'NV': 'Nevada',
  'NH': 'New Hampshire',
  'NJ': 'New Jersey',
  'NM': 'New Mexico',
  'NY': 'New York',
  'NC': 'North Carolina',
  'ND': 'North Dakota',
  'OH': 'Ohio',
  'OK': 'Oklahoma',
  'OR': 'Oregon',
  'PA': 'Pennsylvania',
  'RI': 'Rhode Island',
  'SC': 'South Carolina',
  'SD': 'South Dakota',
  'TN': 'Tennessee',
  'TX': 'Texas',
  'UT': 'Utah',
  'VT': 'Vermont',
  'VA': 'Virginia',
  'WA': 'Washington',
  'WV': 'West Virginia',
  'WI': 'Wisconsin',
  'WY': 'Wyoming'
};

/**
 * Converts state to full name if it's an abbreviation
 */
const getStateFullName = (state: string): string => {
  if (!state) return '';
  const normalized = state.trim().toUpperCase();
  return STATE_ABBREVIATIONS[normalized] || state;
};

/**
 * Validates if a US state is allowed for partners that exclude Alaska/Hawaii.
 * 
 * @param address - The address to validate
 * @returns Object with isValid flag and error message if invalid
 */
export const validateUSStateExcludingAkHi = (
  address: intrfce.iAddressFormData
): { isValid: boolean; errorMessage?: string } => {
  const { countryValue, stateValue } = address;
  
  // Only validate US addresses
  if (!countryValue || (countryValue !== 'US' && countryValue !== 'USA' && countryValue !== 'United States')) {
    return { isValid: true }; // Not a US address, skip validation
  }
  
  // If no state value, skip validation (will be caught by required field validation)
  if (!stateValue || !stateValue.trim()) {
    return { isValid: true };
  }
  
  const stateFullName = getStateFullName(stateValue);
  
  // Check if state is in allowed list (case-insensitive)
  const isAllowed = ALLOWED_US_STATES_EXCLUDING_AK_HI.some(
    allowedState => allowedState.toLowerCase() === stateFullName.toLowerCase()
  );
  
  if (!isAllowed) {
    return {
      isValid: false,
      errorMessage: 'We cannot ship to this state. Please use a different shipping address.'
    };
  }
  
  return { isValid: true };
};

/**
 * Validates if a US state is allowed for all other brands (includes Alaska and Hawaii)
 * 
 * @param address - The address to validate
 * @returns Object with isValid flag and error message if invalid
 */
export const validateUSState = (
  address: intrfce.iAddressFormData
): { isValid: boolean; errorMessage?: string } => {
  const { countryValue, stateValue } = address;

  // Only validate US addresses
  if (!countryValue || (countryValue !== 'US' && countryValue !== 'USA' && countryValue !== 'United States')) {
    return { isValid: true }; // Not a US address, skip validation
  }

  // If no state value, skip validation (will be caught by required field validation)
  if (!stateValue || !stateValue.trim()) {
    return { isValid: true };
  }

  const stateFullName = getStateFullName(stateValue);

  // Check if state is in allowed list (case-insensitive)
  const isAllowed = ALLOWED_US_STATES.some(
    allowedState => allowedState.toLowerCase() === stateFullName.toLowerCase()
  );

  if (!isAllowed) {
    return {
      isValid: false,
      errorMessage: 'We cannot ship to this state. Please use a different shipping address.'
    };
  }

  return { isValid: true };
};
